import React from 'react';
import { motion } from 'framer-motion';
import {
  Award,
  Target,
  Leaf,
  Users,
  Building,
  Microscope,
} from 'lucide-react';

import about from './images1/renvitek_intro.png';
import srinivas from './images1/srinivas.png';
import swetha from './images1/Swetha.jpeg';
import madhavi from './images1/Dr.Madhavi Vedulla.jpeg';
import mahalakshmi from './images1/Dr.mahaLakshmi.jpeg';

const DR_SRINIVAS_IMG = srinivas;
const MS_SWETHA_IMG = swetha;
const DR_MADHAVI_IMG = madhavi;
const DR_MAHALAKSHMI_IMG = mahalakshmi;

/* ================================================================
   Why RENVITEK
================================================================ */

const whyRenvitek = [
  {
    icon: Award,
    title: 'Domain Expertise',
    description:
      'Led by Ph.D.-level professionals with over 15 years of experience in Biogas-CBG establishments, plant operations, engineering, and applied research.',
  },
  {
    icon: Target,
    title: 'Customized Solutions',
    description:
      'Every feedstock is different—we tailor systems to suit local resources, operating requirements, and climatic conditions.',
  },
  {
    icon: Leaf,
    title: 'Sustainability First',
    description:
      'Our approach ensures long-term operability, minimal downtime, efficient resource recovery, and maximum biogas and CBG yield.',
  },
  {
    icon: Microscope,
    title: 'Tech-Driven, Field-Tested',
    description:
      'From biological pre-hydrolysis to early-warning indicators, we integrate scientific innovations with field-tested engineering practices.',
  },
  {
    icon: Building,
    title: 'Strong Local Roots',
    description:
      "Headquartered in Hyderabad, we have a deep understanding of India's regulatory framework, climatic conditions, and industrial landscape.",
  },
  {
    icon: Users,
    title: 'Technical Collaborations',
    description:
      'We collaborate with JNTUH and ASCI–ES Laboratories to deliver comprehensive services spanning design, engineering, execution, process optimization, and performance monitoring.',
  },
];

/* ================================================================
   Core Team

   Each profile contains approximately 120–130 words.
   Expertise for Dr. Madhavi and Dr. Mahalakshmi is included
   naturally within their biography paragraphs.
================================================================ */

const teamMembers = [
  {
    name: 'Dr. Srinivas Varma Jukuri',
    title: 'MD & Chief Scientist',
    image: DR_SRINIVAS_IMG,
    description: [
      `Dr. Srinivas Jukuri is a waste-to-energy and Biogas-CBG technology specialist with over 15 years of experience in the design, commissioning, stabilization, troubleshooting, and optimization of commercial biogas systems. He holds a Ph.D. from JNTU Hyderabad, with research focused on early-warning indicators and biological pre-hydrolysis for improved gas yield and scum control.`,

      `He has contributed to large-scale biogas and CBG projects for GPS, AES, Mahindra Waste to Energy Solutions, and Spantech Engineers, supported by research experience at IICT, BITS Hyderabad, JNTUH, and EPTRI.`,

      `At RENVITEK Engineers LLP, he leads the development of RBHM™ and RRR-DIGESTER™ technologies, applying data-driven monitoring and process optimization to improve methane production, operational stability, plant reliability, and circular-economy performance across diverse organic feedstocks, during commissioning and long-term plant operation.`,
    ],
  },

  {
    name: 'Ms. Swetha Chunchu',
    title: 'Designated Partner',
    image: MS_SWETHA_IMG,
    description: [
      `Ms. Swetha Chunchu is a Scientist and Designated Partner at RENVITEK Engineers LLP, with over 15 years of experience in pharmaceutical and environmental wet-laboratory analysis. She holds an M. Pharmacy in Pharmaceutical Analysis from JNTU Hyderabad and has extensive expertise in operating and interpreting advanced analytical instruments, including GC, HPLC, and related laboratory systems.`,

      `Her work supports analytical accuracy, quality control, process validation, environmental testing, and regulatory compliance for biogas and environmental applications.`,

      `At RENVITEK, she evaluates process samples, validates laboratory results, and provides analytical support for plant performance assessment. By connecting laboratory findings with field-scale engineering requirements, she strengthens scientific decision-making, quality assurance, commissioning support, and routine process monitoring, helping ensure that RENVITEK solutions remain accurate, practical, reliable, and technically defensible.`,
    ],
  },

  {
    name: 'Dr. Madhavi Vedulla, Ph.D.',
    title:
      'Technical Director – Biological Pretreatment & Biofertilizer Enrichment',
    image: DR_MADHAVI_IMG,
    description: [
      `Dr. Madhavi Vedulla is an Industrial Microbiologist with over 15 years of experience in microbial biotechnology, fermentation technology, process optimization, and product commercialization. She holds a Ph.D. in Microbiology and specializes in microbial formulations, industrial fermentation, and sustainable bio-based technologies.`,

      `At RENVITEK Engineers LLP, she leads biological pretreatment solutions for Napier grass, paddy straw, and other lignocellulosic biomass used in Biogas-CBG projects. Her specialized microbial cultures improve substrate biodegradability, increase methane yield, and reduce retention time.`,

      `She also develops microbial consortia for enriching Liquid Organic Fermented Manure and Solid Organic Fermented Manure. Her expertise includes biofertilizers, biopesticides, microbial culture development, agricultural-residue bioconversion, fermentation scale-up, digestate valorisation, technology transfer, analytical microbiology, innovation management, and commercial deployment for scalable circular bioeconomy applications.`,
    ],
  },

  {
    name: 'Dr. L. K. Mahalakshmi, Ph.D.',
    title:
      'Technical Director – Water, Wastewater & Environmental Compliance',
    image: DR_MAHALAKSHMI_IMG,
    description: [
      `Dr. L. K. Mahalakshmi is an Environmental Scientist with over 17 years of experience in water and wastewater management, environmental compliance, sanitation, and environmental biotechnology. She holds a Ph.D. in Environmental Sciences from JNTU Hyderabad and has led research, engineering, and environmental infrastructure projects across India.`,

      `At RENVITEK Engineers LLP, she directs digestate liquid management, effluent treatment, process-water recycling, and MAC water recovery for Biogas-CBG plants. Her work focuses on maximizing water reuse, resource recovery, statutory compliance, and environmental protection.`,

      `She also supports approvals from the Pollution Control Board, NA/NALA authorities, PESO, Factory and Boiler Departments, Ground Water Authority, and related agencies. Her expertise covers ETP/STP systems, Environmental Impact Assessment, regulatory documentation, wastewater process optimization, and sustainable water management for bioenergy projects.`,
    ],
  },
];

/* ================================================================
   About Page
================================================================ */

export const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}

      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              About Us
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              RENVITEK Engineers LLP
            </h1>

            <p className="text-white/70 text-lg max-w-3xl mx-auto">
              A technology-driven organization specializing in Biogas and
              Compressed Bio-Gas systems, with a strong focus on scientific
              process engineering and long-term plant reliability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Content */}

      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                Engineering the Sustainable Future
              </h2>

              <div className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
                <p>
                  RENVITEK Engineers LLP, based at JNTUH, Hyderabad, is a
                  dedicated technology-driven company specializing in Biogas,
                  Compressed Bio-Gas, and wastewater solutions.
                </p>

                <p>
                  We collaborate with JNTUH and ASCI to provide end-to-end
                  solutions for waste-to-energy projects, from design and
                  research to installation, commissioning, performance
                  monitoring, and plant optimization.
                </p>

                <p>
                  The company delivers cost-optimized and scalable solutions for
                  treating biodegradable organic substrates and converting them
                  into renewable gaseous energy through advanced anaerobic
                  digestion pathways.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={about}
                alt="RENVITEK biogas and compressed biogas engineering solutions"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </motion.div>
          </div>

          <motion.div
            className="mt-8"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              <p>
                The RENVITEK team comprises a multidisciplinary group of
                professionals with expertise in biochemical engineering,
                microbiology, environmental science, civil and mechanical
                engineering, laboratory analysis, and field operations.
              </p>

              <p>
                This diverse skill set enables us to approach Biogas-CBG
                projects holistically, from conceptual design to full-scale
                implementation. Our foundation in biochemical process
                engineering, anaerobic digestion technology, and systems
                integration allows us to deliver efficient and sustainable
                solutions.
              </p>

              <p>
                By combining scientific principles with practical engineering,
                we transform organic matter into clean renewable energy while
                promoting circular-economy practices, resource recovery, water
                reuse, and environmental sustainability.
              </p>

              <p>
                RENVITEK is also actively engaged in innovation, operator
                training, academic collaboration, technology transfer, and
                capacity building for a sustainable and energy-resilient
                future.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why RENVITEK */}

      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-amber-500 uppercase tracking-[0.2em] text-sm font-semibold">
              Our Strengths
            </span>

            <h2 className="text-3xl md:text-4xl font-bold text-emerald-900 dark:text-white mt-4">
              Why RENVITEK?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyRenvitek.map((item, index) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={item.title}
                  className="bg-white dark:bg-emerald-900 p-8 shadow-sm hover:shadow-lg transition-shadow"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon className="w-12 h-12 text-emerald-600 dark:text-emerald-400 mb-4" />

                  <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-3">
                    {item.title}
                  </h3>

                  <p className="text-slate-600 dark:text-white/60 leading-relaxed tracking-wider">
                    {item.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Team */}

      <section
        className="py-12 md:py-10 bg-white dark:bg-emerald-950"
        data-testid="team-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-amber-500 uppercase tracking-[0.2em] text-sm font-semibold">
              Leadership
            </span>

            <h2 className="text-3xl md:text-4xl font-bold text-emerald-900 dark:text-white mt-4">
              Core Team
            </h2>
          </motion.div>

          {/*
            Each row automatically uses the height of the longest card
            within that row.

            Row 1 cards remain equal in height.
            Row 2 cards remain equal in height.
            Different rows may have different heights.
          */}

          <div className="grid md:grid-cols-2 gap-12 items-stretch">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                className="team-card h-full flex flex-col bg-white dark:bg-emerald-900 shadow-lg"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                data-testid={`team-member-${index}`}
              >
                <img
                  src={member.image}
                  alt={member.name}
                  loading="lazy"
                  className="w-48 h-48 object-cover object-top mx-auto mb-6 border-4 border-emerald-600 !rounded-[20%]"
                />

                <h3 className="text-2xl font-bold text-emerald-900 dark:text-white mb-2">
                  {member.name}
                </h3>

                <div className="min-h-[72px] flex items-start justify-center">
                  <p className="text-amber-500 font-semibold mb-6 tracking-wider">
                    {member.title}
                  </p>
                </div>

                <div className="flex-1 space-y-4 text-slate-600 dark:text-white/70 text-sm leading-relaxed text-left tracking-wider">
                  {member.description.map((paragraph, paragraphIndex) => (
                    <p key={`${member.name}-${paragraphIndex}`}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships */}

      <section className="py-12 md:py-10 bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-amber-400 uppercase tracking-[0.2em] text-sm font-semibold">
              Collaborations
            </span>

            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-8">
              Joint Ventures and Partnerships
            </h2>

            <p className="text-white/70 max-w-3xl mx-auto tracking-wider">
              We collaborate with JNTUH and ASCI–ES Laboratories to deliver
              comprehensive, high-quality services—spanning design,
              engineering, execution, process optimization, and performance
              monitoring.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;